import React, { useState, useEffect, useMemo } from 'react';
import { FileText, Copy, Download, Shuffle, Settings, Eye, Zap, Type, Hash, List } from 'lucide-react';

interface GenerationOptions {
  startWithLorem: boolean;
  includeHtml: boolean;
  includePunctuation: boolean;
  capitalizeFirst: boolean;
  customLength: boolean;
  minWordsPerSentence: number;
  maxWordsPerSentence: number;
  minSentencesPerParagraph: number;
  maxSentencesPerParagraph: number;
  addLineBreaks: boolean;
  customSeparator: string;
  includeNumbers: boolean;
  mixedCase: boolean;
  removeDuplicates: boolean;
  showWordCount: boolean;
  showCharCount: boolean;
  showParagraphCount: boolean;
}

interface GenerationType {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  unit: string;
}

interface GenerationStats {
  wordCount: number;
  characterCount: number;
  characterCountNoSpaces: number;
  paragraphCount: number;
  sentenceCount: number;
  averageWordsPerSentence: number;
  averageSentencesPerParagraph: number;
  readingTime: number;
}

const LoremIpsumGenerator: React.FC = () => {
  const [generationType, setGenerationType] = useState('paragraphs');
  const [amount, setAmount] = useState(3);
  const [generatedText, setGeneratedText] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showStats, setShowStats] = useState(false);

  const [options, setOptions] = useState<GenerationOptions>({
    startWithLorem: true,
    includeHtml: false,
    includePunctuation: true,
    capitalizeFirst: true,
    customLength: false,
    minWordsPerSentence: 4,
    maxWordsPerSentence: 15,
    minSentencesPerParagraph: 3,
    maxSentencesPerParagraph: 8,
    addLineBreaks: true,
    customSeparator: '\n\n',
    includeNumbers: false,
    mixedCase: false,
    removeDuplicates: false,
    showWordCount: true,
    showCharCount: true,
    showParagraphCount: true,
  });

  const generationTypes: GenerationType[] = [
    {
      id: 'paragraphs',
      name: 'Paragraphs',
      description: 'Generate full paragraphs of Lorem Ipsum',
      icon: <FileText className="w-4 h-4" />,
      color: 'blue',
      unit: 'paragraphs'
    },
    {
      id: 'sentences',
      name: 'Sentences',
      description: 'Generate individual sentences',
      icon: <Type className="w-4 h-4" />,
      color: 'green',
      unit: 'sentences'
    },
    {
      id: 'words',
      name: 'Words',
      description: 'Generate specific number of words',
      icon: <Hash className="w-4 h-4" />,
      color: 'purple',
      unit: 'words'
    },
    {
      id: 'lists',
      name: 'Lists',
      description: 'Generate bulleted or numbered lists',
      icon: <List className="w-4 h-4" />,
      color: 'orange',
      unit: 'list items'
    },
    {
      id: 'bytes',
      name: 'Bytes',
      description: 'Generate text of specific byte length',
      icon: <Zap className="w-4 h-4" />,
      color: 'red',
      unit: 'bytes'
    }
  ];

  // Extended Lorem Ipsum word bank
  const loremWords = [
    'lorem', 'ipsum', 'dolor', 'sit', 'amet', 'consectetur', 'adipiscing', 'elit',
    'sed', 'do', 'eiusmod', 'tempor', 'incididunt', 'ut', 'labore', 'et', 'dolore',
    'magna', 'aliqua', 'enim', 'ad', 'minim', 'veniam', 'quis', 'nostrud',
    'exercitation', 'ullamco', 'laboris', 'nisi', 'aliquip', 'ex', 'ea', 'commodo',
    'consequat', 'duis', 'aute', 'irure', 'in', 'reprehenderit', 'voluptate',
    'velit', 'esse', 'cillum', 'fugiat', 'nulla', 'pariatur', 'excepteur', 'sint',
    'occaecat', 'cupidatat', 'non', 'proident', 'sunt', 'culpa', 'qui', 'officia',
    'deserunt', 'mollit', 'anim', 'id', 'est', 'laborum', 'at', 'vero', 'eos',
    'accusamus', 'accusantium', 'doloremque', 'laudantium', 'totam', 'rem',
    'aperiam', 'eaque', 'ipsa', 'quae', 'ab', 'illo', 'inventore', 'veritatis',
    'et', 'quasi', 'architecto', 'beatae', 'vitae', 'dicta', 'explicabo', 'nemo',
    'ipsam', 'quia', 'voluptas', 'aspernatur', 'aut', 'odit', 'fugit', 'sed',
    'quia', 'consequuntur', 'magni', 'dolores', 'ratione', 'sequi', 'nesciunt',
    'neque', 'porro', 'quisquam', 'dolorem', 'adipisci', 'numquam', 'eius',
    'modi', 'tempora', 'incidunt', 'magnam', 'quaerat', 'voluptatem', 'fuga',
    'harum', 'quidem', 'rerum', 'facilis', 'expedita', 'distinctio', 'nam',
    'libero', 'tempore', 'cum', 'soluta', 'nobis', 'eligendi', 'optio',
    'cumque', 'nihil', 'impedit', 'quo', 'minus', 'maxime', 'placeat', 'facere',
    'possimus', 'omnis', 'assumenda', 'repellendus', 'temporibus', 'autem',
    'quibusdam', 'officiis', 'debitis', 'saepe', 'eveniet', 'voluptates',
    'repudiandae', 'recusandae', 'itaque', 'earum', 'hic', 'tenetur', 'sapiente',
    'delectus', 'reiciendis', 'maiores', 'alias', 'perferendis', 'doloribus',
    'asperiores', 'repellat'
  ];

  const generateRandomNumber = (min: number, max: number): number => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const getRandomWords = (count: number, startWithLorem: boolean = false): string[] => {
    const words: string[] = [];
    const availableWords = [...loremWords];
    
    if (startWithLorem && count > 0) {
      words.push('Lorem');
      count--;
    }

    for (let i = 0; i < count; i++) {
      const randomIndex = Math.floor(Math.random() * availableWords.length);
      const word = availableWords[randomIndex];
      
      if (options.removeDuplicates) {
        availableWords.splice(randomIndex, 1);
        if (availableWords.length === 0) {
          availableWords.push(...loremWords);
        }
      }
      
      words.push(word);
    }

    return words;
  };

  const capitalizeSentence = (sentence: string): string => {
    if (!sentence) return sentence;
    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
  };

  const generateSentence = (wordCount?: number): string => {
    const minWords = options.customLength ? options.minWordsPerSentence : 4;
    const maxWords = options.customLength ? options.maxWordsPerSentence : 15;
    const numWords = wordCount || generateRandomNumber(minWords, maxWords);
    
    let words = getRandomWords(numWords);
    
    if (options.includeNumbers) {
      // Replace some words with numbers occasionally
      words = words.map(word => {
        if (Math.random() < 0.1) { // 10% chance
          return Math.floor(Math.random() * 1000).toString();
        }
        return word;
      });
    }

    if (options.mixedCase) {
      words = words.map(word => {
        if (Math.random() < 0.3) { // 30% chance for mixed case
          return word.split('').map(char => 
            Math.random() < 0.5 ? char.toUpperCase() : char.toLowerCase()
          ).join('');
        }
        return word;
      });
    }

    let sentence = words.join(' ');
    
    if (options.capitalizeFirst) {
      sentence = capitalizeSentence(sentence);
    }
    
    if (options.includePunctuation) {
      const punctuation = ['.', '.', '.', '!', '?']; // Weighted toward periods
      sentence += punctuation[Math.floor(Math.random() * punctuation.length)];
    }
    
    return sentence;
  };

  const generateParagraph = (): string => {
    const minSentences = options.customLength ? options.minSentencesPerParagraph : 3;
    const maxSentences = options.customLength ? options.maxSentencesPerParagraph : 8;
    const numSentences = generateRandomNumber(minSentences, maxSentences);
    
    const sentences: string[] = [];
    for (let i = 0; i < numSentences; i++) {
      const isFirst = i === 0 && options.startWithLorem;
      sentences.push(generateSentence());
    }
    
    return sentences.join(' ');
  };

  const generateWords = (count: number): string => {
    const words = getRandomWords(count, options.startWithLorem);
    return words.join(' ');
  };

  const generateList = (itemCount: number): string => {
    const items: string[] = [];
    for (let i = 0; i < itemCount; i++) {
      const sentence = generateSentence();
      const cleanSentence = sentence.replace(/[.!?]$/, ''); // Remove ending punctuation
      
      if (options.includeHtml) {
        items.push(`<li>${cleanSentence}</li>`);
      } else {
        items.push(`• ${cleanSentence}`);
      }
    }
    
    if (options.includeHtml) {
      return `<ul>\n${items.join('\n')}\n</ul>`;
    }
    
    return items.join('\n');
  };

  const generateByBytes = (byteCount: number): string => {
    let text = '';
    while (text.length < byteCount) {
      const sentence = generateSentence();
      text += sentence + ' ';
    }
    return text.substring(0, byteCount).trim();
  };

  const generateText = (): string => {
    let result = '';
    
    switch (generationType) {
      case 'paragraphs':
        const paragraphs: string[] = [];
        for (let i = 0; i < amount; i++) {
          let paragraph = generateParagraph();
          if (options.includeHtml) {
            paragraph = `<p>${paragraph}</p>`;
          }
          paragraphs.push(paragraph);
        }
        result = paragraphs.join(options.addLineBreaks ? '\n\n' : options.customSeparator);
        break;
        
      case 'sentences':
        const sentences: string[] = [];
        for (let i = 0; i < amount; i++) {
          sentences.push(generateSentence());
        }
        result = sentences.join(' ');
        break;
        
      case 'words':
        result = generateWords(amount);
        break;
        
      case 'lists':
        result = generateList(amount);
        break;
        
      case 'bytes':
        result = generateByBytes(amount);
        break;
        
      default:
        result = generateParagraph();
    }
    
    return result;
  };

  const calculateStats = (text: string): GenerationStats => {
    const words = text.replace(/<[^>]*>/g, '').split(/\s+/).filter(word => word.length > 0);
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 0);
    
    const wordCount = words.length;
    const characterCount = text.length;
    const characterCountNoSpaces = text.replace(/\s/g, '').length;
    const paragraphCount = paragraphs.length;
    const sentenceCount = sentences.length;
    const averageWordsPerSentence = sentenceCount > 0 ? Math.round(wordCount / sentenceCount) : 0;
    const averageSentencesPerParagraph = paragraphCount > 0 ? Math.round(sentenceCount / paragraphCount) : 0;
    const readingTime = Math.ceil(wordCount / 200); // Assuming 200 words per minute
    
    return {
      wordCount,
      characterCount,
      characterCountNoSpaces,
      paragraphCount,
      sentenceCount,
      averageWordsPerSentence,
      averageSentencesPerParagraph,
      readingTime
    };
  };

  const stats = useMemo(() => calculateStats(generatedText), [generatedText]);

  useEffect(() => {
    setGeneratedText(generateText());
  }, [generationType, amount, options]);

  const copyToClipboard = async () => {
    if (generatedText) {
      await navigator.clipboard.writeText(generatedText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const downloadText = () => {
    if (generatedText) {
      const summary = `Lorem Ipsum Generated Text
Type: ${generationTypes.find(t => t.id === generationType)?.name}
Amount: ${amount} ${generationTypes.find(t => t.id === generationType)?.unit}
Word Count: ${stats.wordCount}
Character Count: ${stats.characterCount}
Paragraph Count: ${stats.paragraphCount}

Generated Text:
${generatedText}`;

      const blob = new Blob([summary], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lorem-ipsum-text.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const regenerateText = () => {
    setGeneratedText(generateText());
  };

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-3">
            <FileText className="text-indigo-600" />
            Lorem Ipsum Generator
          </h1>
          <p className="text-gray-600">Generate placeholder text for your designs and prototypes</p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Generation Settings</h2>
            <div className="flex gap-2">
              <button
                onClick={regenerateText}
                className="flex items-center gap-2 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-sm"
              >
                <Shuffle size={16} />
                Regenerate
              </button>
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
              >
                <Settings size={16} />
                Options
              </button>
              <button
                onClick={() => setShowStats(!showStats)}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                <Eye size={16} />
                Statistics
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                Amount of {generationTypes.find(t => t.id === generationType)?.unit}
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                value={amount}
                onChange={(e) => setAmount(parseInt(e.target.value) || 1)}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none"
              />
            </div>
            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                <p>Type: <span className="font-semibold">{generationTypes.find(t => t.id === generationType)?.name}</span></p>
                <p>Generated: <span className="font-semibold text-indigo-600">{stats.wordCount} words</span></p>
              </div>
            </div>
          </div>
        </div>

        {/* Generation Options */}
        {showOptions && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-800">Generation Options</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Text Options</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.startWithLorem}
                    onChange={(e) => setOptions({...options, startWithLorem: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Start with "Lorem ipsum"</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.capitalizeFirst}
                    onChange={(e) => setOptions({...options, capitalizeFirst: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Capitalize First Letter</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.includePunctuation}
                    onChange={(e) => setOptions({...options, includePunctuation: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Include Punctuation</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.includeNumbers}
                    onChange={(e) => setOptions({...options, includeNumbers: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Include Numbers</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.mixedCase}
                    onChange={(e) => setOptions({...options, mixedCase: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Mixed Case</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Structure</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.customLength}
                    onChange={(e) => setOptions({...options, customLength: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Custom Length Rules</span>
                </label>

                {options.customLength && (
                  <>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Words per Sentence</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="1"
                          max="50"
                          value={options.minWordsPerSentence}
                          onChange={(e) => setOptions({...options, minWordsPerSentence: parseInt(e.target.value) || 1})}
                          className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                          placeholder="Min"
                        />
                        <input
                          type="number"
                          min="1"
                          max="50"
                          value={options.maxWordsPerSentence}
                          onChange={(e) => setOptions({...options, maxWordsPerSentence: parseInt(e.target.value) || 15})}
                          className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                          placeholder="Max"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Sentences per Paragraph</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="1"
                          max="20"
                          value={options.minSentencesPerParagraph}
                          onChange={(e) => setOptions({...options, minSentencesPerParagraph: parseInt(e.target.value) || 1})}
                          className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                          placeholder="Min"
                        />
                        <input
                          type="number"
                          min="1"
                          max="20"
                          value={options.maxSentencesPerParagraph}
                          onChange={(e) => setOptions({...options, maxSentencesPerParagraph: parseInt(e.target.value) || 8})}
                          className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                          placeholder="Max"
                        />
                      </div>
                    </div>
                  </>
                )}

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.removeDuplicates}
                    onChange={(e) => setOptions({...options, removeDuplicates: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Remove Duplicate Words</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Output Format</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.includeHtml}
                    onChange={(e) => setOptions({...options, includeHtml: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Include HTML Tags</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.addLineBreaks}
                    onChange={(e) => setOptions({...options, addLineBreaks: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Add Line Breaks</span>
                </label>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Custom Separator</label>
                  <input
                    type="text"
                    value={options.customSeparator}
                    onChange={(e) => setOptions({...options, customSeparator: e.target.value})}
                    placeholder="Custom separator..."
                    disabled={options.addLineBreaks}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm disabled:bg-gray-100"
                  />
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-600">Show in Stats</h4>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={options.showWordCount}
                      onChange={(e) => setOptions({...options, showWordCount: e.target.checked})}
                      className="w-3 h-3 text-purple-600"
                    />
                    <span className="text-xs">Word Count</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={options.showCharCount}
                      onChange={(e) => setOptions({...options, showCharCount: e.target.checked})}
                      className="w-3 h-3 text-purple-600"
                    />
                    <span className="text-xs">Character Count</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={options.showParagraphCount}
                      onChange={(e) => setOptions({...options, showParagraphCount: e.target.checked})}
                      className="w-3 h-3 text-purple-600"
                    />
                    <span className="text-xs">Paragraph Count</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Generation Types */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Type className="text-indigo-600" />
            <h2 className="text-xl font-semibold text-gray-800">Choose Generation Type</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {generationTypes.map((type) => (
              <div
                key={type.id}
                onClick={() => setGenerationType(type.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  generationType === type.id
                    ? `border-${type.color}-500 ${getColorClasses(type.color)} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {type.icon}
                    <h3 className={`font-semibold ${
                      generationType === type.id ? '' : 'text-gray-800'
                    }`}>
                      {type.name}
                    </h3>
                  </div>
                  {generationType === type.id && (
                    <div className={`w-3 h-3 bg-${type.color}-500 rounded-full`}></div>
                  )}
                </div>
                <p className={`text-sm ${
                  generationType === type.id ? '' : 'text-gray-600'
                }`}>
                  {type.description}
                </p>
              </div>
            ))}
          </div>

          {/* Type Help */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <h3 className="font-semibold text-gray-700 mb-2">
              {generationTypes.find(t => t.id === generationType)?.name} Help
            </h3>
            <div className="text-sm text-gray-600">
              {generationType === 'paragraphs' && (
                <div>
                  <p>• Generate complete paragraphs with multiple sentences</p>
                  <p>• Perfect for filling content areas and layouts</p>
                </div>
              )}
              {generationType === 'sentences' && (
                <div>
                  <p>• Generate individual sentences</p>
                  <p>• Useful for headlines, captions, and short text</p>
                </div>
              )}
              {generationType === 'words' && (
                <div>
                  <p>• Generate a specific number of words</p>
                  <p>• Great for precise text length requirements</p>
                </div>
              )}
              {generationType === 'lists' && (
                <div>
                  <p>• Generate bulleted or numbered list items</p>
                  <p>• Optional HTML list formatting available</p>
                </div>
              )}
              {generationType === 'bytes' && (
                <div>
                  <p>• Generate text of a specific byte length</p>
                  <p>• Useful for testing character limits and data constraints</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Output Area */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <label className="text-lg font-semibold text-gray-700">Generated Text</label>
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-500">
                {stats.wordCount} words • {stats.characterCount} chars
              </div>
              <button
                onClick={copyToClipboard}
                disabled={!generatedText}
                className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50"
              >
                <Copy size={16} />
                {copySuccess ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={downloadText}
                disabled={!generatedText}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm disabled:opacity-50"
              >
                <Download size={16} />
                Download
              </button>
            </div>
          </div>
          
          <textarea
            value={generatedText}
            readOnly
            className="w-full h-80 p-4 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-700 resize-none text-sm leading-relaxed"
            placeholder="Generated Lorem Ipsum text will appear here..."
          />
        </div>

        {/* Statistics */}
        {showStats && generatedText && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Text Statistics</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {options.showWordCount && (
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <h3 className="font-semibold text-blue-800 mb-2">Words</h3>
                  <div className="text-2xl font-bold text-blue-900">{stats.wordCount}</div>
                  <p className="text-sm text-blue-600">Total word count</p>
                </div>
              )}

              {options.showCharCount && (
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <h3 className="font-semibold text-green-800 mb-2">Characters</h3>
                  <div className="text-2xl font-bold text-green-900">{stats.characterCount}</div>
                  <p className="text-sm text-green-600">{stats.characterCountNoSpaces} without spaces</p>
                </div>
              )}

              {options.showParagraphCount && (
                <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                  <h3 className="font-semibold text-purple-800 mb-2">Paragraphs</h3>
                  <div className="text-2xl font-bold text-purple-900">{stats.paragraphCount}</div>
                  <p className="text-sm text-purple-600">Text blocks</p>
                </div>
              )}

              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-semibold text-orange-800 mb-2">Sentences</h3>
                <div className="text-2xl font-bold text-orange-900">{stats.sentenceCount}</div>
                <p className="text-sm text-orange-600">Total sentences</p>
              </div>
            </div>

            {/* Additional Stats */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Avg Words/Sentence</h4>
                <div className="text-lg font-bold text-gray-900">{stats.averageWordsPerSentence}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Avg Sentences/Paragraph</h4>
                <div className="text-lg font-bold text-gray-900">{stats.averageSentencesPerParagraph}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Reading Time</h4>
                <div className="text-lg font-bold text-gray-900">{stats.readingTime} min</div>
              </div>
            </div>

            {/* Generation Details */}
            <div className="mt-6">
              <h3 className="font-semibold text-gray-700 mb-3">Generation Details:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <h4 className="font-semibold text-indigo-800 mb-1">Type</h4>
                  <div className="text-sm text-indigo-700">
                    {generationTypes.find(t => t.id === generationType)?.name} ({amount} {generationTypes.find(t => t.id === generationType)?.unit})
                  </div>
                </div>
                <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                  <h4 className="font-semibold text-teal-800 mb-1">Options</h4>
                  <div className="text-sm text-teal-700">
                    {[
                      options.startWithLorem && 'Lorem start',
                      options.includeHtml && 'HTML tags',
                      options.includePunctuation && 'Punctuation',
                      options.includeNumbers && 'Numbers',
                      options.mixedCase && 'Mixed case',
                      options.removeDuplicates && 'No duplicates'
                    ].filter(Boolean).join(', ') || 'Default settings'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Presets */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Zap className="text-yellow-600" />
            <h2 className="text-xl font-semibold text-gray-800">Quick Presets</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => {
                setGenerationType('paragraphs');
                setAmount(3);
                setOptions({...options, startWithLorem: true, includeHtml: false});
              }}
              className="p-4 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors text-left"
            >
              <h3 className="font-semibold text-blue-800 mb-1">Standard</h3>
              <p className="text-sm text-blue-600">3 paragraphs, classic Lorem Ipsum</p>
            </button>

            <button
              onClick={() => {
                setGenerationType('paragraphs');
                setAmount(5);
                setOptions({...options, startWithLorem: true, includeHtml: true});
              }}
              className="p-4 bg-green-50 border border-green-200 rounded-xl hover:bg-green-100 transition-colors text-left"
            >
              <h3 className="font-semibold text-green-800 mb-1">HTML</h3>
              <p className="text-sm text-green-600">5 paragraphs with HTML tags</p>
            </button>

            <button
              onClick={() => {
                setGenerationType('sentences');
                setAmount(10);
                setOptions({...options, startWithLorem: false, includeHtml: false});
              }}
              className="p-4 bg-purple-50 border border-purple-200 rounded-xl hover:bg-purple-100 transition-colors text-left"
            >
              <h3 className="font-semibold text-purple-800 mb-1">Short Text</h3>
              <p className="text-sm text-purple-600">10 sentences for captions</p>
            </button>

            <button
              onClick={() => {
                setGenerationType('lists');
                setAmount(8);
                setOptions({...options, startWithLorem: false, includeHtml: true});
              }}
              className="p-4 bg-orange-50 border border-orange-200 rounded-xl hover:bg-orange-100 transition-colors text-left"
            >
              <h3 className="font-semibold text-orange-800 mb-1">List Items</h3>
              <p className="text-sm text-orange-600">8 HTML list items</p>
            </button>
          </div>

          {/* Usage Tips */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <h3 className="font-semibold text-gray-700 mb-2">💡 Usage Tips</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p>• Use "Start with Lorem" for traditional placeholder text</p>
              <p>• Enable HTML tags for web development and markup</p>
              <p>• Custom length rules help match your design requirements</p>
              <p>• Byte generation is perfect for testing character limits</p>
              <p>• Statistics help ensure your text fits design constraints</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoremIpsumGenerator;